/**
 * iter187 — OtaLogo.
 *
 * Renders an **image-only** OTA logo (height 20px, object-fit contain) —
 * no text label, no coloured pill. Bundle-shipped PNG/JPEG assets live
 * under `/public/ota-logos/`. If a matching logo isn't available for the
 * given OTA (or the browser fails to load the image), we fall back to a
 * plain text label of the OTA name so nothing ever renders blank.
 *
 * Walk-in / Offline / Direct don't map to a real OTA — they render as
 * a small icon + text as before.
 *
 * Backward compatibility: accepts both `otaName` and `source` props;
 * unused legacy `size` / `className` / `showLabel` props are tolerated.
 */

import React, { useState } from 'react';
import { Home, Globe } from 'lucide-react';

const _slug = (name) =>
  String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Slug → public asset URL. Extended aliases keep the panel's variant
// spellings mapped to the same image.
const OTA_LOGOS = {
  agoda:       '/ota-logos/agoda.jpeg',
  airbnb:      '/ota-logos/airbnb.png',
  booking:     '/ota-logos/booking.jpeg',
  bookingcom:  '/ota-logos/booking.jpeg',
  cleartrip:   '/ota-logos/cleartrip.jpeg',
  easemytrip:  '/ota-logos/easemytrip.jpeg',
  easymytrip:  '/ota-logos/easemytrip.jpeg',   // common typo / older spelling
  emt:         '/ota-logos/easemytrip.jpeg',
  expedia:     '/ota-logos/expedia.jpeg',
  goibibo:     '/ota-logos/goibibo.png',
  makemytrip:  '/ota-logos/makemytrip.jpeg',
  mmt:         '/ota-logos/makemytrip.jpeg',
  yatra:       '/ota-logos/yatra.jpeg',
  tripcom:     '/ota-logos/tripcom.jpeg',
  trip:        '/ota-logos/tripcom.jpeg',
};

const _imgStyle = {
  height: '28px',
  width: 'auto',
  maxWidth: '100px',
  objectFit: 'contain',
  display: 'inline-block',
  verticalAlign: 'middle',
};

const _fallbackTextClass = 'inline-flex items-center text-xs font-medium text-gray-800';

const OtaLogo = ({ otaName, source }) => {
  const name = (otaName ?? source ?? '').trim() || 'Offline';
  const slug = _slug(name);
  const [failed, setFailed] = useState(false);

  // Walk-in — house icon (not an OTA).
  if (slug === 'walkin' || slug === 'walkinoffline') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"
        data-testid="ota-badge-walkin"
      >
        <Home className="w-4 h-4" strokeWidth={2} />
        <span>Walk-in</span>
      </span>
    );
  }

  // Offline / Direct — neutral icon + text (not an OTA).
  if (slug === 'offline' || slug === 'direct') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600"
        data-testid="ota-badge-offline"
      >
        <Globe className="w-4 h-4" strokeWidth={2} />
        <span>{name === 'Direct' ? 'Direct' : 'Offline'}</span>
      </span>
    );
  }

  const src = OTA_LOGOS[slug];
  const testid = `ota-logo-${slug || 'unknown'}`;

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        style={_imgStyle}
        loading="lazy"
        onError={() => setFailed(true)}
        data-testid={testid}
      />
    );
  }

  // No image asset available (or the image failed to load) — fall back
  // to a plain text label per product spec.
  return (
    <span className={_fallbackTextClass} data-testid={testid}>
      {name}
    </span>
  );
};

export default OtaLogo;
